import React, { ReactNode, Suspense, useContext } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import ImagePreview from './component/image-preview-v2/ImagePreview';
import {
	ImageDetails,
	ImagePreviewContext,
} from './component/image-preview-v2/imagePreview.context';
import AppRoutes from './route';

import 'reactjs-popup/dist/index.css';
import 'react-toastify/dist/ReactToastify.min.css';
import './app.scss';
import { withProfiler } from '@sentry/react';
import { DocumentViewerContext } from './context/DocumentViewerContext';
import useDocViewer from './hooks/useDocViewer';
import {
	Box,
	ToastProvider,
	joyThemeTokens,
	materialThemeTokens,
} from '@farmart-engineering/central-component-library';
import { extendTheme as extendJoyTheme } from '@mui/joy/styles';
import { extendTheme as extendMaterialTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import {
	CssBaseline as JoyCssBaseline,
	CssVarsProvider as JoyCssVarsProvider,
	ThemeProvider as JoyThemeProvider,
} from '@mui/joy';
import '@fontsource/noto-sans';
import { DocumentViewerContextType } from './types/documentViewerTypes';
import { MaintenanceWindowGate } from '@farmart-engineering/brave-ship-react';
import MaintenanceWindow from './patterns/template/maintainanceWindow/MaintenanceWindow';

const joyTheme = extendJoyTheme(joyThemeTokens);
const materialTheme = extendMaterialTheme(materialThemeTokens);
const theme = deepmerge(materialTheme, joyTheme);

const DocumentViewerLazyLoaded = React.lazy(
	() => import('./patterns/molecule/DocumentViewer/DocumentViewer'),
);
interface DocumentViewerProviderProps {
	children: ReactNode;
}

const DocumentViewerProvider: React.FC<DocumentViewerProviderProps> = ({
	children,
}) => {
	const {
		docViewerData,
		setActiveDoc,
		handleAddDoc,
		handleAddDocs,
		handleCloseDocViewer,
		renderFooter,
		handleSetActiveDoc,
		updateDocConfigs,
		updateFooterProps,
		moveNextDoc,
		resetDocConfigs,
		updateActiveDocData,
	} = useDocViewer();

	return (
		<DocumentViewerContext.Provider
			value={{
				handleAddDoc,
				handleAddDocs,
				handleCloseDocViewer,
				renderFooter,
				updateDocConfigs,
				resetDocConfigs,
				docViewerData,
				moveNextDoc,
				setActiveDoc,
				updateFooterProps,
				activeDoc: docViewerData?.activeDoc,
				handleSetActiveDoc,
				updateActiveDocData,
			}}
		>
			{children}
		</DocumentViewerContext.Provider>
	);
};

const App = () => {
	const imagePreviewProps = useContext(ImagePreviewContext);
	return (
		<Suspense fallback={<p>Loading</p>}>
			<DocumentViewerProvider>
				<div className='App'>
					<BrowserRouter>
						<JoyCssVarsProvider theme={theme}>
							<JoyThemeProvider theme={theme}>
								<JoyCssBaseline>
									<ToastProvider>
										<MaintenanceWindowGate
											strategy='always'
											renderMaintenanceWindow={data => <MaintenanceWindow {...data} />}
											connection='WS'
											featureName='fmt_os'
											scope='fmt_os'
										>
											<AppRoutes />
										</MaintenanceWindowGate>
									</ToastProvider>
								</JoyCssBaseline>
							</JoyThemeProvider>
						</JoyCssVarsProvider>
					</BrowserRouter>
					<ToastContainer />
					{!!imagePreviewProps && (
						<ImagePreview<ImageDetails>
							hideDrawer={imagePreviewProps.hideDrawer}
							show={imagePreviewProps.show}
							onHide={imagePreviewProps.onHide}
							galleryImages={imagePreviewProps.galleryImages}
							onChange={imagePreviewProps.onChange}
							currentIndex={imagePreviewProps.currentIndex}
							imageKey={imagePreviewProps.imageKey}
							nameKey={imagePreviewProps.nameKey}
						>
							{imagePreviewProps.children}
						</ImagePreview>
					)}
					<DocViewer />
				</div>
			</DocumentViewerProvider>
		</Suspense>
	);
};

const DocViewer = () => {
	const { docViewerData, setActiveDoc, handleCloseDocViewer } = useContext(
		DocumentViewerContext,
	) as DocumentViewerContextType;

	if (docViewerData.docs.length === 0) return null;

	return (
		<Box
			height='100dvh'
			position='fixed'
			top='0'
			left='0'
			bottom='0'
			right='0'
			//z index is set to 999 to ensure the toast is always on top of the doc viewer
			zIndex='999'
		>
			<DocumentViewerLazyLoaded
				onClose={handleCloseDocViewer}
				setActiveDoc={setActiveDoc}
				testId='global-doc-viewer'
				{...docViewerData}
			/>
		</Box>
	);
};

export default withProfiler(App);
